import { PDFDocument, degrees, rgb, StandardFonts } from 'pdf-lib';
import * as pdfjs from 'pdfjs-dist/build/pdf.mjs';
import { formatBytes, saveBlob } from './extra-common.js';

pdfjs.GlobalWorkerOptions.workerSrc = '/assets/vendor/pdfjs/pdf.worker.min.mjs';

export function getPdfDocumentParams(source) {
  const base = {
    cMapUrl: '/assets/vendor/pdfjs/cmaps/',
    cMapPacked: true,
    standardFontDataUrl: '/assets/vendor/pdfjs/standard_fonts/',
    wasmUrl: '/assets/vendor/pdfjs/wasm/',
    disableFontFace: true,
    useSystemFonts: false,
    enableXfa: true,
    fontExtraProperties: true
  };
  if (source instanceof Uint8Array) {
    return { ...base, data: source.slice() };
  }
  if (source instanceof ArrayBuffer) {
    return { ...base, data: source.slice(0) };
  }
  if (typeof source === 'object' && source !== null) {
    if (source.data instanceof Uint8Array) {
      return { ...base, ...source, data: source.data.slice() };
    }
    if (source.data instanceof ArrayBuffer) {
      return { ...base, ...source, data: source.data.slice(0) };
    }
    return { ...base, ...source };
  }
  return { ...base, data: source };
}

export { PDFDocument, degrees, rgb, StandardFonts, pdfjs };

export function parsePageSelection(value, pageCount) {
  if (!value || !value.trim()) return Array.from({ length: pageCount }, (_, index) => index);
  const pages = new Set();
  for (const part of value.split(',').map((item) => item.trim()).filter(Boolean)) {
    const match = part.match(/^(\d+)\s*(?:-\s*(\d+))?$/);
    if (!match) throw new Error(`Invalid page range: ${part}`);
    const first = Number(match[1]);
    const last = Number(match[2] || match[1]);
    if (first < 1 || last < first || last > pageCount) throw new Error(`Pages must be between 1 and ${pageCount}.`);
    for (let page = first; page <= last; page++) pages.add(page - 1);
  }
  if (!pages.size) throw new Error('Enter at least one page.');
  return [...pages].sort((a, b) => a - b);
}

export function formatPageSelection(indices) {
  if (!indices || !indices.length) return '';
  const sorted = [...new Set(indices)].map((i) => i + 1).sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === prev + 1) {
      prev = sorted[i];
    } else {
      ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
      start = sorted[i];
      prev = sorted[i];
    }
  }
  ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
  return ranges.join(', ');
}

export function formatError(error, isAr) {
  const details = error?.message || String(error);
  if (/encrypted|password/i.test(details)) return isAr ? 'الملف محمي بكلمة مرور، وهو غير مدعوم.' : 'This PDF is password-protected and is not supported.';
  return isAr ? `تعذرت معالجة الملف: ${details}` : `Could not process the PDF: ${details}`;
}

export async function renderPdfPageToCanvas(page, canvas, targetWidth = 180, rotationOffset = 0) {
  if (canvas._renderTask) {
    try { canvas._renderTask.cancel(); } catch { /* ignore */ }
    canvas._renderTask = null;
  }
  const rotation = ((page.rotate || 0) + rotationOffset) % 360;
  const baseViewport = page.getViewport({ scale: 1, rotation });
  const scale = targetWidth / baseViewport.width;
  const viewport = page.getViewport({ scale, rotation });

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.max(1, Math.floor(viewport.width * dpr));
  canvas.height = Math.max(1, Math.floor(viewport.height * dpr));
  const isMainCanvas = canvas.classList.contains('pdf-main-canvas') ||
                       canvas.id === 'pdf-preview-canvas' ||
                       canvas.id === 'po-preview';
  if (isMainCanvas) {
    canvas.style.maxWidth = '100%';
    canvas.style.maxHeight = '62vh';
    canvas.style.width = 'auto';
    canvas.style.height = 'auto';
    canvas.style.aspectRatio = `${viewport.width} / ${viewport.height}`;
    canvas.style.margin = '0 auto';
    canvas.style.display = 'block';
  } else {
    canvas.style.maxWidth = '100%';
    canvas.style.width = `${Math.min(viewport.width, 104)}px`;
    canvas.style.height = 'auto';
    canvas.style.aspectRatio = `${viewport.width} / ${viewport.height}`;
  }

  const ctx = canvas.getContext('2d');
  ctx.save();
  ctx.scale(dpr, dpr);
  const task = page.render({ canvasContext: ctx, viewport });
  canvas._renderTask = task;
  try {
    await task.promise;
  } catch (error) {
    if (error?.name !== 'RenderingCancelledException') throw error;
  } finally {
    if (canvas._renderTask === task) canvas._renderTask = null;
    ctx.restore();
  }
}

/**
 * Interactive Visual PDF Page Selector
 * Provides thumbnail grid, click-to-select, presets (All, Odd, Even, Invert),
 * range picker, per-page rotation, and two-way sync with the manual text input.
 */
export class PdfPageSelector {
  constructor({
    gridEl,
    rangeInput,
    fromInput,
    toInput,
    applyRangeBtn,
    summaryEl,
    hintEl,
    countEl,
    presetBtns = [],
    isAr = false,
    onChange = () => {}
  }) {
    this.gridEl = gridEl;
    this.rangeInput = rangeInput;
    this.fromInput = fromInput;
    this.toInput = toInput;
    this.applyRangeBtn = applyRangeBtn;
    this.summaryEl = summaryEl;
    this.hintEl = hintEl;
    this.countEl = countEl;
    this.presetBtns = presetBtns;
    this.isAr = isAr;
    this.onChange = onChange;

    this.pdfDoc = null;
    this.pageCount = 0;
    this.selectedIndices = new Set();
    this.rotations = [];
    this.mode = 'extract';
    this.observer = null;
    this.cards = [];
    this.isSyncingInput = false;

    this._bindControls();
  }

  _bindControls() {
    if (this.rangeInput) {
      this.rangeInput.addEventListener('input', () => {
        if (this.isSyncingInput || !this.pageCount) return;
        try {
          const parsed = parsePageSelection(this.rangeInput.value, this.pageCount);
          this.selectedIndices = new Set(parsed);
          this._updateCardStates();
          this._updateSummary();
          this.onChange(this.getSelectedIndices(), this.rotations);
        } catch {
          // Keep current state while typing incomplete syntax
        }
      });
    }

    if (this.applyRangeBtn && this.fromInput && this.toInput) {
      this.applyRangeBtn.addEventListener('click', () => {
        if (!this.pageCount) return;
        const from = Math.max(1, Math.min(this.pageCount, Number(this.fromInput.value) || 1));
        const to = Math.max(from, Math.min(this.pageCount, Number(this.toInput.value) || from));
        this.selectedIndices.clear();
        for (let p = from; p <= to; p++) this.selectedIndices.add(p - 1);
        this._syncText();
        this._updateCardStates();
        this._updateSummary();
        this.onChange(this.getSelectedIndices(), this.rotations);
      });

      [this.fromInput, this.toInput].forEach((inp) => {
        inp.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.applyRangeBtn.click();
          }
        });
      });
    }

    this.presetBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!this.pageCount) return;
        const preset = btn.dataset.preset;
        this.applyPreset(preset);
      });
    });
  }

  applyPreset(preset) {
    if (preset === 'all') {
      this.selectedIndices = new Set(Array.from({ length: this.pageCount }, (_, i) => i));
    } else if (preset === 'none') {
      this.selectedIndices.clear();
    } else if (preset === 'odd') {
      this.selectedIndices = new Set(Array.from({ length: this.pageCount }, (_, i) => i).filter((i) => (i + 1) % 2 === 1));
    } else if (preset === 'even') {
      this.selectedIndices = new Set(Array.from({ length: this.pageCount }, (_, i) => i).filter((i) => (i + 1) % 2 === 0));
    } else if (preset === 'invert') {
      const next = new Set();
      for (let i = 0; i < this.pageCount; i++) {
        if (!this.selectedIndices.has(i)) next.add(i);
      }
      this.selectedIndices = next;
    }
    this._syncText();
    this._updateCardStates();
    this._updateSummary();
    this.onChange(this.getSelectedIndices(), this.rotations);
  }

  mount(pdfDoc, pageCount, initialMode = 'extract') {
    this.destroy();
    this.pdfDoc = pdfDoc;
    this.pageCount = pageCount;
    this.mode = initialMode;
    this.selectedIndices = new Set(Array.from({ length: pageCount }, (_, i) => i));
    this.pageOrder = Array.from({ length: pageCount }, (_, i) => i);
    this.rotations = new Array(pageCount).fill(0);
    this.cards = [];

    if (this.fromInput) { this.fromInput.value = '1'; this.fromInput.max = String(pageCount); }
    if (this.toInput) { this.toInput.value = String(Math.min(5, pageCount)); this.toInput.max = String(pageCount); }
    if (this.countEl) { this.countEl.textContent = String(pageCount); }

    this.gridEl.replaceChildren();

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const card = entry.target;
            const index = Number(card.dataset.index);
            this.observer?.unobserve(card);
            this._renderPageThumbnail(card, index);
          }
        });
      }, { rootMargin: '250px 0px' });
    }

    for (let index = 0; index < pageCount; index++) {
      const card = this._createCard(index);
      this.cards.push(card);
      this.gridEl.appendChild(card);
      if (this.observer) {
        this.observer.observe(card);
      } else {
        this._renderPageThumbnail(card, index);
      }
    }

    this._syncText();
    this._updateCardStates();
    this._updateSummary();
    this.onChange(this.getSelectedIndices(), this.rotations);
  }

  _createCard(index) {
    const card = document.createElement('div');
    card.className = 'pdf-page-card';
    card.dataset.index = String(index);
    card.dataset.page = String(index + 1);
    card.setAttribute('role', 'checkbox');
    card.setAttribute('aria-checked', 'true');
    card.setAttribute('tabindex', '0');

    // Header bar inside card
    const header = document.createElement('div');
    header.className = 'pdf-card-top';

    const checkWrap = document.createElement('label');
    checkWrap.className = 'pdf-check-wrap';
    const check = document.createElement('input');
    check.type = 'checkbox';
    check.className = 'pdf-card-check';
    check.checked = true;
    check.setAttribute('aria-label', this.isAr ? `تحديد الصفحة ${index + 1}` : `Select page ${index + 1}`);
    checkWrap.appendChild(check);

    const numBadge = document.createElement('span');
    numBadge.className = 'pdf-page-badge';
    numBadge.textContent = String(index + 1);

    const statusBadge = document.createElement('span');
    statusBadge.className = 'pdf-page-status-badge';

    const rotateBtn = document.createElement('button');
    rotateBtn.type = 'button';
    rotateBtn.className = 'pdf-card-rotate-btn';
    rotateBtn.title = this.isAr ? 'تدوير هذه الصفحة 90°' : 'Rotate this page 90°';
    rotateBtn.setAttribute('aria-label', this.isAr ? `تدوير الصفحة ${index + 1}` : `Rotate page ${index + 1}`);
    rotateBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><polyline points="21 3 21 8 16 8"/></svg>';

    rotateBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.rotations[index] = (this.rotations[index] + 90) % 360;
      this._renderPageThumbnail(card, index);
      this._updateCardStates();
      this.onChange(this.getSelectedIndices(), this.rotations);
    });

    header.append(checkWrap, numBadge, statusBadge, rotateBtn);

    // Canvas thumbnail box
    const thumbWrap = document.createElement('div');
    thumbWrap.className = 'pdf-card-thumb-wrap';

    const shimmer = document.createElement('div');
    shimmer.className = 'pdf-card-loading-shimmer';
    thumbWrap.appendChild(shimmer);

    const canvas = document.createElement('canvas');
    canvas.className = 'pdf-card-canvas';
    thumbWrap.appendChild(canvas);

    card.append(header, thumbWrap);

    // Click handler to toggle selection
    const toggle = () => {
      if (this.selectedIndices.has(index)) {
        this.selectedIndices.delete(index);
      } else {
        this.selectedIndices.add(index);
      }
      this._syncText();
      this._updateCardStates();
      this._updateSummary();
      this.onChange(this.getSelectedIndices(), this.rotations);
    };

    card.addEventListener('click', (e) => {
      if (e.target.closest('.pdf-card-rotate-btn')) return;
      toggle();
    });

    card.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        toggle();
      }
    });

    card.draggable = true;
    card.addEventListener('dragstart', (e) => {
      e.dataTransfer.setData('text/plain', String(index));
      card.classList.add('is-dragging');
    });
    card.addEventListener('dragend', () => {
      card.classList.remove('is-dragging');
    });
    card.addEventListener('dragover', (e) => {
      e.preventDefault();
      card.classList.add('is-drag-over');
    });
    card.addEventListener('dragleave', () => {
      card.classList.remove('is-drag-over');
    });
    card.addEventListener('drop', (e) => {
      e.preventDefault();
      card.classList.remove('is-drag-over');
      const fromIdx = Number(e.dataTransfer.getData('text/plain'));
      if (!isNaN(fromIdx) && fromIdx !== index) {
        this.reorderPages(fromIdx, index);
      }
    });

    return card;
  }

  reorderPages(fromIdx, toIdx) {
    if (!this.pageOrder) return;
    const fromPos = this.pageOrder.indexOf(fromIdx);
    const toPos = this.pageOrder.indexOf(toIdx);
    if (fromPos === -1 || toPos === -1 || fromPos === toPos) return;

    const moved = this.pageOrder.splice(fromPos, 1)[0];
    this.pageOrder.splice(toPos, 0, moved);

    this.gridEl.replaceChildren();
    this.cards = [];
    for (const originalIdx of this.pageOrder) {
      const card = this._createCard(originalIdx);
      this.cards.push(card);
      this.gridEl.appendChild(card);
      this._renderPageThumbnail(card, originalIdx);
    }
    this._updateCardStates();
    this._updateSummary();
    this.onChange(this.getSelectedIndices(), this.rotations);
  }

  async _renderPageThumbnail(card, index) {
    if (!this.pdfDoc) return;
    const canvas = card.querySelector('.pdf-card-canvas');
    const shimmer = card.querySelector('.pdf-card-loading-shimmer');
    if (!canvas) return;

    try {
      const page = await this.pdfDoc.getPage(index + 1);
      await renderPdfPageToCanvas(page, canvas, 170, this.rotations[index] || 0);
      if (shimmer) shimmer.hidden = true;
    } catch {
      if (shimmer) shimmer.hidden = true;
    }
  }

  setMode(mode) {
    this.mode = mode;
    this._updateCardStates();
    this._updateSummary();
  }

  setGlobalRotation(angle) {
    for (const index of this.selectedIndices) {
      this.rotations[index] = angle;
    }
    // Re-render affected cards
    this.selectedIndices.forEach((index) => {
      const card = this.cards[index];
      if (card) this._renderPageThumbnail(card, index);
    });
    this._updateCardStates();
    this.onChange(this.getSelectedIndices(), this.rotations);
  }

  _syncText() {
    if (!this.rangeInput) return;
    this.isSyncingInput = true;
    this.rangeInput.value = formatPageSelection([...this.selectedIndices]);
    this.isSyncingInput = false;
  }

  _updateCardStates() {
    this.cards.forEach((card, index) => {
      const isSelected = this.selectedIndices.has(index);
      const check = card.querySelector('.pdf-card-check');
      const badge = card.querySelector('.pdf-page-status-badge');
      const rotateBtn = card.querySelector('.pdf-card-rotate-btn');
      const rotation = this.rotations[index] || 0;

      if (check) check.checked = isSelected;
      card.setAttribute('aria-checked', String(isSelected));

      // Reset state classes
      card.classList.remove('is-selected', 'is-unselected', 'is-removed', 'is-kept');

      if (this.mode === 'extract') {
        rotateBtn.hidden = true;
        if (isSelected) {
          card.classList.add('is-selected');
          if (badge) { badge.textContent = this.isAr ? 'مستخرجة' : 'Extract'; badge.className = 'pdf-page-status-badge badge-extract'; }
        } else {
          card.classList.add('is-unselected');
          if (badge) { badge.textContent = ''; badge.className = 'pdf-page-status-badge'; }
        }
      } else if (this.mode === 'remove') {
        rotateBtn.hidden = true;
        if (isSelected) {
          card.classList.add('is-removed');
          if (badge) { badge.textContent = this.isAr ? 'حذف' : 'Remove'; badge.className = 'pdf-page-status-badge badge-remove'; }
        } else {
          card.classList.add('is-kept');
          if (badge) { badge.textContent = this.isAr ? 'إبقاء' : 'Keep'; badge.className = 'pdf-page-status-badge badge-keep'; }
        }
      } else if (this.mode === 'rotate') {
        rotateBtn.hidden = false;
        if (isSelected) card.classList.add('is-selected');
        if (badge) {
          badge.textContent = rotation ? `${rotation}°` : '';
          badge.className = rotation ? 'pdf-page-status-badge badge-rotate' : 'pdf-page-status-badge';
        }
      }
    });
  }

  _updateSummary() {
    const selCount = this.selectedIndices.size;
    const total = this.pageCount;

    if (this.summaryEl) {
      if (this.isAr) {
        this.summaryEl.textContent = `تم تحديد ${selCount} من أصل ${total} صفحة`;
      } else {
        this.summaryEl.textContent = `${selCount} of ${total} page(s) selected`;
      }
    }

    if (this.hintEl) {
      if (this.mode === 'extract') {
        this.hintEl.textContent = this.isAr
          ? (selCount ? `سيتم استخراج ${selCount} صفحة وحفظها في مستند PDF مستقل.` : 'اختر صفحة واحدة على الأقل للاستخراج.')
          : (selCount ? `${selCount} page(s) will be extracted into a new PDF document.` : 'Select at least one page to extract.');
      } else if (this.mode === 'remove') {
        const remaining = total - selCount;
        this.hintEl.textContent = this.isAr
          ? (remaining > 0 ? `سيتم حذف ${selCount} صفحة والاحتفاظ بـ ${remaining} صفحة في المستند الناتج.` : 'لا يمكن حذف جميع الصفحات، يجب إبقاء صفحة واحدة على الأقل.')
          : (remaining > 0 ? `${selCount} page(s) will be deleted; ${remaining} page(s) will be kept in the output.` : 'Cannot remove all pages; at least one must remain.');
      } else if (this.mode === 'rotate') {
        this.hintEl.textContent = this.isAr
          ? `يمكنك تدوير كل صفحة منفردة بزر ↻ أو تحديد الصفحات وتطبيق زاوية التدوير عليها.`
          : `Rotate individual pages using the ↻ button or select pages to apply rotation.`;
      }
    }
  }

  getSelectedIndices() {
    if (this.pageOrder && this.pageOrder.length) {
      return this.pageOrder.filter((idx) => this.selectedIndices.has(idx));
    }
    return [...this.selectedIndices].sort((a, b) => a - b);
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.cards.forEach((card) => {
      const canvas = card.querySelector('.pdf-card-canvas');
      if (canvas?._renderTask) {
        try { canvas._renderTask.cancel(); } catch { /* ignore */ }
      }
    });
    this.cards = [];
    this.gridEl?.replaceChildren();
  }
}

/**
 * Interactive Pre-Download PDF Result Viewer
 * Renders the generated PDF in high resolution with page flipping,
 * document facts, zoom fit, and direct download.
 */
export class PdfResultViewer {
  constructor({
    canvas,
    prevBtn,
    nextBtn,
    currentNumEl,
    totalPagesEl,
    statsEl,
    loadingEl,
    downloadBtn,
    backBtn,
    isAr = false,
    onBack = () => {}
  }) {
    this.canvas = canvas;
    this.prevBtn = prevBtn;
    this.nextBtn = nextBtn;
    this.currentNumEl = currentNumEl;
    this.totalPagesEl = totalPagesEl;
    this.statsEl = statsEl;
    this.loadingEl = loadingEl;
    this.downloadBtn = downloadBtn;
    this.backBtn = backBtn;
    this.isAr = isAr;
    this.onBack = onBack;

    this.previewDoc = null;
    this.pdfBytes = null;
    this.filename = 'document.pdf';
    this.currentPage = 1;
    this.totalPages = 1;

    this._bindControls();
  }

  _bindControls() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.renderCurrentPage();
        }
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        if (this.currentPage < this.totalPages) {
          this.currentPage++;
          this.renderCurrentPage();
        }
      });
    }

    if (this.downloadBtn) {
      this.downloadBtn.addEventListener('click', () => {
        if (!this.pdfBytes) return;
        const blob = new Blob([this.pdfBytes], { type: 'application/pdf' });
        saveBlob(blob, this.filename);
      });
    }

    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.onBack());
    }
  }

  async loadBytes(uint8Array, filename = 'document.pdf') {
    this.pdfBytes = uint8Array;
    this.filename = filename;
    if (this.loadingEl) this.loadingEl.hidden = false;

    if (this.previewDoc) {
      try { await this.previewDoc.destroy(); } catch { /* ignore */ }
      this.previewDoc = null;
    }

    try {
      // Pass a clone so web-worker transfer doesn't detach the buffer
      const clone = uint8Array.slice(0);
      this.previewDoc = await pdfjs.getDocument(getPdfDocumentParams({ data: clone })).promise;
      this.totalPages = this.previewDoc.numPages;
      this.currentPage = 1;

      if (this.totalPagesEl) this.totalPagesEl.textContent = String(this.totalPages);
      if (this.statsEl) {
        this.statsEl.textContent = this.isAr
          ? `${this.totalPages} صفحة · ${formatBytes(uint8Array.byteLength)}`
          : `${this.totalPages} pages · ${formatBytes(uint8Array.byteLength)}`;
      }

      await this.renderCurrentPage();
    } finally {
      if (this.loadingEl) this.loadingEl.hidden = true;
    }
  }

  async renderCurrentPage() {
    if (!this.previewDoc || !this.canvas) return;
    if (this.currentNumEl) this.currentNumEl.textContent = String(this.currentPage);
    if (this.prevBtn) this.prevBtn.disabled = this.currentPage <= 1;
    if (this.nextBtn) this.nextBtn.disabled = this.currentPage >= this.totalPages;

    try {
      const page = await this.previewDoc.getPage(this.currentPage);
      // High-resolution render fitting standard container width (up to 720px)
      await renderPdfPageToCanvas(page, this.canvas, 720, 0);
      const container = this.canvas.closest('.pdf-canvas-container');
      if (container) container.scrollTop = 0;
    } catch {
      // Ignore cancellation
    }
  }

  destroy() {
    if (this.previewDoc) {
      try { this.previewDoc.destroy(); } catch { /* ignore */ }
      this.previewDoc = null;
    }
    this.pdfBytes = null;
  }
}

export async function applyPageNumbers(pdfDoc, options = {}) {
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const total = pdfDoc.getPageCount();
  for (let i = 0; i < total; i++) {
    const page = pdfDoc.getPage(i);
    const { width } = page.getSize();
    const text = `${i + 1} / ${total}`;
    const textSize = 10;
    const textWidth = font.widthOfTextAtSize(text, textSize);
    page.drawText(text, {
      x: width / 2 - textWidth / 2,
      y: 20,
      size: textSize,
      font,
      color: rgb(0.35, 0.35, 0.35)
    });
  }
}

export async function applyWatermark(pdfDoc, text, opacity = 0.2) {
  if (!text) return;
  const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const total = pdfDoc.getPageCount();
  for (let i = 0; i < total; i++) {
    const page = pdfDoc.getPage(i);
    const { width, height } = page.getSize();
    const size = Math.min(width, height) / 10;
    const textWidth = font.widthOfTextAtSize(text, size);
    page.drawText(text, {
      x: width / 2 - textWidth / 3,
      y: height / 2,
      size,
      font,
      color: rgb(0.7, 0.7, 0.7),
      rotate: degrees(45),
      opacity
    });
  }
}


